/** เรนเดอร์ข้อความไทยด้วย Canvas → PNG สำหรับฝังใน pdf-lib (หลีกเลี่ยงสระ/วรรณยุกต์เพี้ยน) */

export const SARABUN_FONT_URL = "/assets/fonts/Sarabun-Regular.ttf";
const SARABUN_FONT_FACE_NAME = "SarabunPDF";
const CANVAS_SCALE = 2;

export type RenderThaiTextOptions = {
  maxWidthPt?: number;
  /** ความกว้างภาพคงที่ (ใช้จัดกึ่งกลาง/ขวา) */
  widthPt?: number;
  textAlign?: "left" | "center" | "right";
  color?: string;
  backgroundColor?: string;
  paddingHPx?: number;
  paddingTopPx?: number;
  paddingBottomPx?: number;
};

/** แถวสรุปแบบโมดัล: ป้าย | จำนวน | ตัวเลข | หน่วย */
export type ModalSummaryPdfRow = {
  label: string;
  value: string;
  unit: string;
  valueColor?: string;
};

export type TableColAlign = "left" | "center" | "right";

export type ThaiTextPngResult = {
  bytes: Uint8Array;
  widthPt: number;
  heightPt: number;
};

async function ensureSarabunFontLoaded(): Promise<void> {
  if (typeof document === "undefined") return;
  const hasSarabun = Array.from(document.fonts.values()).some(
    (f) => f.family === SARABUN_FONT_FACE_NAME
  );
  if (hasSarabun) return;
  const fontUrl = `${window.location.origin}${SARABUN_FONT_URL}`;
  const fontFace = new FontFace(SARABUN_FONT_FACE_NAME, `url(${fontUrl})`);
  const loaded = await fontFace.load();
  document.fonts.add(loaded);
}

function ptToPx(pt: number): number {
  return Math.round(pt * CANVAS_SCALE);
}

function pxToPt(px: number): number {
  return px / CANVAS_SCALE;
}

/** ระยะบรรทัดสำหรับไทย — เว้นสระ/วรรณยุกต์พอดี ไม่ห่างเกินไป */
function thaiLineHeightPx(fontSizePx: number): number {
  return Math.round(fontSizePx * 1.38) + 2;
}

/** ตัดบรรทัดตาม grapheme (รองรับสระ/วรรณยุกต์ไทย) */
export function wrapThaiTextToLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidthPx: number
): string[] {
  const normalized = String(text ?? "").trim() || "-";
  const segmenter =
    typeof Intl !== "undefined" && "Segmenter" in Intl
      ? new Intl.Segmenter("th", { granularity: "grapheme" })
      : null;
  const graphemes = segmenter
    ? Array.from(segmenter.segment(normalized), (s) => s.segment)
    : Array.from(normalized);

  const lines: string[] = [];
  let current = "";
  for (const g of graphemes) {
    const trial = current + g;
    if (ctx.measureText(trial).width <= maxWidthPx) {
      current = trial;
    } else {
      if (current) lines.push(current);
      current = g;
    }
  }
  if (current) lines.push(current);
  return lines.length ? lines : ["-"];
}

function canvasToPngBytes(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas toBlob failed"));
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
      },
      "image/png",
      1
    );
  });
}

/** ข้อความบรรทัดเดียว (หรือหลายบรรทัดถ้าระบุ maxWidthPt) */
export async function renderThaiTextToPng(
  text: string,
  fontSizePt: number,
  options?: RenderThaiTextOptions
): Promise<ThaiTextPngResult> {
  await ensureSarabunFontLoaded();
  const fontSizePx = ptToPx(fontSizePt);
  const paddingH = options?.paddingHPx ?? 6;
  const paddingTop = options?.paddingTopPx ?? 6;
  const paddingBottom = options?.paddingBottomPx ?? 6;
  const color = options?.color ?? "#000000";
  const bg = options?.backgroundColor ?? "#ffffff";
  const textAlign = options?.textAlign ?? "left";
  const fixedWidthPx =
    options?.widthPt != null ? ptToPx(options.widthPt) : undefined;
  const maxWidthPx =
    options?.maxWidthPt != null
      ? ptToPx(options.maxWidthPt) - paddingH * 2
      : fixedWidthPx != null
        ? fixedWidthPx - paddingH * 2
        : undefined;

  const measureCanvas = document.createElement("canvas");
  const measureCtx = measureCanvas.getContext("2d");
  if (!measureCtx) throw new Error("Canvas 2d not available");
  measureCtx.font = `${fontSizePx}px ${SARABUN_FONT_FACE_NAME}, sans-serif`;

  const lines =
    maxWidthPx != null
      ? wrapThaiTextToLines(measureCtx, text, maxWidthPx)
      : [String(text ?? "").trim() || "-"];

  const lineHeightPx = thaiLineHeightPx(fontSizePx);
  let widthPx = fixedWidthPx ?? paddingH * 2;
  if (fixedWidthPx == null) {
    for (const line of lines) {
      widthPx = Math.max(widthPx, Math.ceil(measureCtx.measureText(line).width) + paddingH * 2);
    }
    if (maxWidthPx != null) {
      widthPx = Math.max(widthPx, maxWidthPx + paddingH * 2);
    }
  }
  const heightPx = paddingTop + lines.length * lineHeightPx + paddingBottom;

  const canvas = document.createElement("canvas");
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2d not available");
  ctx.font = `${fontSizePx}px ${SARABUN_FONT_FACE_NAME}, sans-serif`;
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, widthPx, heightPx);
  ctx.fillStyle = color;
  ctx.textBaseline = "top";
  lines.forEach((line, i) => {
    const normalized = line.normalize("NFC");
    const textWidth = ctx.measureText(normalized).width;
    let x = paddingH;
    if (textAlign === "center") {
      x = (widthPx - textWidth) / 2;
    } else if (textAlign === "right") {
      x = widthPx - paddingH - textWidth;
    }
    ctx.fillText(normalized, x, paddingTop + i * lineHeightPx);
  });

  const bytes = await canvasToPngBytes(canvas);
  return { bytes, widthPt: pxToPt(widthPx), heightPt: pxToPt(heightPx) };
}

/** แถวตารางหลายคอลัมน์ในภาพเดียว */
export async function renderThaiTableRowToPng(
  cells: string[],
  colWidthsPt: number[],
  fontSizePt: number,
  options?: {
    lineHeightPt?: number;
    color?: string;
    colors?: string[];
    alignments?: TableColAlign[];
  }
): Promise<ThaiTextPngResult> {
  await ensureSarabunFontLoaded();
  const fontSizePx = ptToPx(fontSizePt);
  const lineHeightPt = options?.lineHeightPt ?? fontSizePt + 5;
  const lineHeightPx = Math.max(ptToPx(lineHeightPt), thaiLineHeightPx(fontSizePx));
  const padV = 5;
  const tableWidthPx = ptToPx(colWidthsPt.reduce((a, b) => a + b, 0));

  const measureCanvas = document.createElement("canvas");
  const measureCtx = measureCanvas.getContext("2d");
  if (!measureCtx) throw new Error("Canvas 2d not available");
  measureCtx.font = `normal ${fontSizePx}px ${SARABUN_FONT_FACE_NAME}, sans-serif`;

  const colLines = cells.map((cell, i) =>
    wrapThaiTextToLines(measureCtx, cell, ptToPx(colWidthsPt[i]) - 6)
  );
  const rowLines = Math.max(1, ...colLines.map((l) => l.length));
  const heightPx = padV * 2 + rowLines * lineHeightPx;

  const canvas = document.createElement("canvas");
  canvas.width = tableWidthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2d not available");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, tableWidthPx, heightPx);
  ctx.font = `normal ${fontSizePx}px ${SARABUN_FONT_FACE_NAME}, sans-serif`;
  ctx.textBaseline = "top";

  let xPx = 0;
  colLines.forEach((lines, colIdx) => {
    const colWidthPx = ptToPx(colWidthsPt[colIdx]);
    ctx.fillStyle = options?.colors?.[colIdx] ?? options?.color ?? "#000000";
    lines.forEach((line, lineIdx) => {
      const normalized = line.normalize("NFC");
      const textWidth = ctx.measureText(normalized).width;
      const align = options?.alignments?.[colIdx] ?? "left";
      let textX = xPx + 3;
      if (align === "right") {
        textX = xPx + colWidthPx - 3 - textWidth;
      } else if (align === "center") {
        textX = xPx + (colWidthPx - textWidth) / 2;
      }
      ctx.fillText(normalized, textX, padV + lineIdx * lineHeightPx);
    });
    xPx += colWidthPx;
  });

  const bytes = await canvasToPngBytes(canvas);
  return { bytes, widthPt: pxToPt(tableWidthPx), heightPt: pxToPt(heightPx) };
}

/** บรรทัดเดียว: ข้อความซ้าย + ข้อความขวา */
export async function renderThaiLeftRightLineToPng(
  leftText: string,
  rightText: string,
  fontSizePt: number,
  widthPt: number
): Promise<ThaiTextPngResult> {
  await ensureSarabunFontLoaded();
  const fontSizePx = ptToPx(fontSizePt);
  const widthPx = ptToPx(widthPt);
  const paddingH = 6;
  const paddingTop = 6;
  const paddingBottom = 6;
  const lineHeightPx = thaiLineHeightPx(fontSizePx);
  const heightPx = paddingTop + lineHeightPx + paddingBottom;

  const canvas = document.createElement("canvas");
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2d not available");
  ctx.font = `${fontSizePx}px ${SARABUN_FONT_FACE_NAME}, sans-serif`;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, widthPx, heightPx);
  ctx.textBaseline = "top";

  const left = leftText.normalize("NFC");
  const right = rightText.normalize("NFC");
  ctx.fillStyle = "#000000";
  ctx.fillText(left, paddingH, paddingTop);
  const rightW = ctx.measureText(right).width;
  ctx.fillText(right, widthPx - paddingH - rightW, paddingTop);

  const bytes = await canvasToPngBytes(canvas);
  return { bytes, widthPt, heightPt: pxToPt(heightPx) };
}

/**
 * สรุปแบบ grid ในโมดัล (4 คอลัมน์: ป้าย | จำนวน | ตัวเลข | หน่วย) — ไม่มีกรอบ
 */
export async function renderThaiModalSummaryGridToPng(
  rows: ModalSummaryPdfRow[],
  widthPt: number,
  fontSizePt: number
): Promise<ThaiTextPngResult> {
  await ensureSarabunFontLoaded();
  const fontSizePx = ptToPx(fontSizePt);
  const widthPx = ptToPx(widthPt);
  const colGapPx = ptToPx(11);
  const rowGapPx = ptToPx(6);
  /** เผื่อสระ/วรรณยุกต์ด้านบน–ล่าง (โดยเฉพาะแถวแรกไม่ให้โดนตัดขอบ canvas) */
  const padTopPx = 12;
  const padBottomPx = 8;
  const lineHeightPx = thaiLineHeightPx(fontSizePx) + 2;
  const contentWidthPx = widthPx;
  const gapsTotal = colGapPx * 3;
  const col0W = Math.round((contentWidthPx - gapsTotal) * 0.64);
  const col1W = Math.round((contentWidthPx - gapsTotal) * 0.085);
  const col2W = Math.round((contentWidthPx - gapsTotal) * 0.11);
  const col3W = contentWidthPx - gapsTotal - col0W - col1W - col2W;
  const colWidthsPx = [col0W, col1W, col2W, col3W];
  const colX = [
    0,
    col0W + colGapPx,
    col0W + colGapPx + col1W + colGapPx,
    col0W + colGapPx + col1W + colGapPx + col2W + colGapPx,
  ];

  const measureCanvas = document.createElement("canvas");
  const measureCtx = measureCanvas.getContext("2d");
  if (!measureCtx) throw new Error("Canvas 2d not available");
  measureCtx.font = `${fontSizePx}px ${SARABUN_FONT_FACE_NAME}, sans-serif`;

  const rowLineCounts = rows.map((row) => {
    const counts = [
      wrapThaiTextToLines(measureCtx, row.label, colWidthsPx[0] - 4).length,
      1,
      wrapThaiTextToLines(measureCtx, row.value, colWidthsPx[2] - 4).length,
      wrapThaiTextToLines(measureCtx, row.unit, colWidthsPx[3] - 4).length,
    ];
    return Math.max(1, ...counts);
  });

  const heightPx =
    padTopPx +
    padBottomPx +
    rowLineCounts.reduce((sum, n) => sum + n * lineHeightPx, 0) +
    Math.max(0, rows.length - 1) * rowGapPx;

  const canvas = document.createElement("canvas");
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2d not available");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, widthPx, heightPx);
  ctx.font = `${fontSizePx}px ${SARABUN_FONT_FACE_NAME}, sans-serif`;
  ctx.textBaseline = "top";

  let y = padTopPx;
  rows.forEach((row, rowIdx) => {
    const rowBlockH = rowLineCounts[rowIdx] * lineHeightPx;
    const cells = [
      { text: row.label, color: "#000000", align: "left" as TableColAlign },
      { text: "จำนวน", color: "#666666", align: "left" as TableColAlign },
      { text: row.value, color: row.valueColor ?? "#000000", align: "right" as TableColAlign },
      { text: row.unit, color: "#000000", align: "left" as TableColAlign },
    ];
    cells.forEach((cell, colIdx) => {
      const lines = wrapThaiTextToLines(measureCtx, cell.text, colWidthsPx[colIdx] - 4);
      ctx.fillStyle = cell.color;
      lines.forEach((line, lineIdx) => {
        const normalized = line.normalize("NFC");
        const tw = ctx.measureText(normalized).width;
        const colW = colWidthsPx[colIdx];
        let x = colX[colIdx] + 2;
        if (cell.align === "right") {
          x = colX[colIdx] + colW - 2 - tw;
        } else if (cell.align === "center") {
          x = colX[colIdx] + (colW - tw) / 2;
        }
        const lineY = y + lineIdx * lineHeightPx;
        ctx.fillText(normalized, x, lineY);
      });
    });
    y += rowBlockH + (rowIdx < rows.length - 1 ? rowGapPx : 0);
  });

  const bytes = await canvasToPngBytes(canvas);
  return { bytes, widthPt, heightPt: pxToPt(heightPx) };
}
